import { RouteObject } from 'react-router-dom';

import { config } from '@/config';
import { type FeatureFlagName, FeatureFlags } from '@/global-hooks/use-feature-flag-enabled';
import { Permission } from '@/shared/modules/permissions';
import { UserRole } from '@/stores/slices/selected-user-config';

import { attemptChunkReloadForError, reportChunkLoadError } from './chunk-load-recovery';
import { PagePaths } from './page-paths';

export type RouteType = {
  importPath: string;
  /**
   * Permission the session must hold to reach this route (GP-3729). A readonly
   * array means the session may hold any one of its permissions. Enforced by
   * `RequirePermission` in `./helpers`, which redirects to the root and toasts
   * on denial.
   *
   * Omit for routes legitimately available to any authenticated user — this gate
   * is additive to whatever the page already checks for itself, never a
   * replacement for it.
   */
  permission?: Permission | readonly Permission[];
  /**
   * Feature flag gating this route. Enforced by `RequireFeatureFlag` in
   * `./helpers`. What the flag reads as while unresolved is the flag's own
   * policy (see `FLAG_POLICY` in `use-feature-flag-enabled.ts`), not a
   * per-route choice.
   *
   * Declared here rather than inside the page so the page component stays free of
   * routing concerns and keeps rendering in Storybook, which has no PostHog.
   */
  featureFlag?: FeatureFlagName;
  /** Where a closed `featureFlag` sends the visitor. Defaults to the Funds page. */
  featureFlagRedirectTo?: string;
  /**
   * Active account session the route requires (GP-3897). Enforced by
   * `RequireSession` in `./helpers`.
   *
   * Needed on top of `permission` for the Gamma admin surfaces: a Gamma team
   * member holds their gamma-plane permissions in every session, so without
   * this they can reach these pages from their Personal Account by typing the
   * URL even though the sidebar only offers them in the admin session.
   */
  session?: UserRole | readonly UserRole[];
} & RouteObject;

export const withMainLayoutRoutes: RouteType[] = [
  { path: PagePaths.settings, importPath: 'settings' },

  { path: PagePaths.createEntity, importPath: 'create-entity' },
  { path: PagePaths.createEntityId, importPath: 'create-entity' },
  { path: PagePaths.createEntityAccount, importPath: 'entity-account' },
  { path: PagePaths.manageEntity, importPath: 'create-entity' },
  {
    path: PagePaths.manageEntityId,
    importPath: 'create-entity',
    permission: Permission.EntityView,
  },
  { path: PagePaths.manageEntityReview, importPath: 'create-entity' },

  { path: PagePaths.setupFund, importPath: 'setup-fund', permission: Permission.FundManage },
  { path: PagePaths.setupFundId, importPath: 'setup-fund', permission: Permission.FundView },
  { path: PagePaths.setupFundIdEdit, importPath: 'setup-fund', permission: Permission.FundView },
  { path: PagePaths.setupFundReview, importPath: 'setup-fund', permission: Permission.FundView },
  { path: PagePaths.fundDashboard, importPath: 'fund-dashboard' },
  {
    path: PagePaths.fundDashboardId,
    importPath: 'fund-dashboard/_id',
    permission: Permission.FundView,
  },
  {
    path: PagePaths.manageTeam,
    importPath: 'manage-team',
    permission: Permission.EntityView,
  },
  // Intentionally ungated: the page self-guards with two `<Navigate>` checks.
  { path: PagePaths.manageTeamEntityId, importPath: 'manage-team-entity' },
  { path: PagePaths.gammaRoles, importPath: 'gamma-roles', session: 'admin' },

  { path: PagePaths.allFunds, importPath: 'all-funds' },
  { path: PagePaths.allFundsId, importPath: 'all-funds-redesign/_id' },
  { path: PagePaths.allFundsFeeder, importPath: 'all-funds-redesign/_id' },
  { path: PagePaths.allFundsInvest, importPath: 'all-funds-redesign/_id/invest' },

  {
    path: PagePaths.overview,
    importPath: 'overview',
    permission: Permission.AdminConsoleView,
    session: 'admin',
  },
  {
    path: PagePaths.overviewEntity,
    importPath: 'overview/_entities-tab/_id',
    permission: Permission.AdminConsoleView,
    session: 'admin',
  },
  {
    path: PagePaths.overviewFund,
    importPath: 'overview/_funds-tab/_id',
    permission: Permission.AdminConsoleView,
    session: 'admin',
  },

  { path: PagePaths.portfolio, importPath: 'portfolio' },
  {
    path: PagePaths.portfolioSend,
    importPath: 'portfolio/send',
    featureFlag: FeatureFlags.CRYPTO_WALLET,
    featureFlagRedirectTo: `${PagePaths.portfolio}?tab=my-assets`,
  },
  {
    path: PagePaths.portfolioReceive,
    importPath: 'portfolio/receive',
    featureFlag: FeatureFlags.CRYPTO_WALLET,
    featureFlagRedirectTo: `${PagePaths.portfolio}?tab=my-assets`,
  },

  {
    path: PagePaths.events,
    importPath: 'events',
    featureFlag: FeatureFlags.GAMMA_EVENTS_ENABLED,
  },
  {
    // Deliberately no `session`: unlike the other Gamma admin surfaces, the people this page is
    // for are event hosts, who are not Gamma team members and work in their ordinary session.
    // Requiring an admin session would lock out exactly its intended users. One permission
    // suffices because every role holding `ViewAndManageGammaEvents` also holds this one.
    path: PagePaths.eventsManaging,
    importPath: 'events/managing',
    permission: Permission.ViewAndManageOwnEvents,
    featureFlag: FeatureFlags.GAMMA_EVENTS_ENABLED,
  },
  {
    path: PagePaths.eventsCreate,
    importPath: 'events/create',
    permission: Permission.ViewAndManageOwnEvents,
    featureFlag: FeatureFlags.GAMMA_EVENTS_ENABLED,
  },
  {
    path: PagePaths.eventsCreateId,
    importPath: 'events/create',
    permission: Permission.ViewAndManageOwnEvents,
    featureFlag: FeatureFlags.GAMMA_EVENTS_ENABLED,
  },
  {
    path: PagePaths.eventsId,
    importPath: 'events/_id',
    featureFlag: FeatureFlags.GAMMA_EVENTS_ENABLED,
  },

  {
    path: PagePaths.investors,
    importPath: 'investors',
    permission: Permission.EntityViewFinancials,
  },

  {
    path: PagePaths.gammaTeamManagement,
    importPath: 'gamma-team-management',
    permission: [
      Permission.GammaTeamView,
      Permission.GammaViewRoles,
      Permission.GammaApproveRoleRequests,
    ],
    session: 'admin',
  },
  // Intentionally ungated: not linked from any sidebar and its required
  // permission is unclear — gating it would be a guess.
  { path: PagePaths.investmentPipeline, importPath: 'pipelines/investment' },
  // Gated inside the page via useFeatureFlagRouteGate, not the declarative featureFlag field —
  // the page itself picks investor vs entity scope from session state, so the gate lives next to
  // that logic rather than the route registry.
  { path: PagePaths.investingBoard, importPath: 'investing-board' },
  {
    path: PagePaths.communications,
    importPath: 'communications-v2',
    permission: Permission.ChatView,
  },
  // Keep the former v2 URL as a harmless compatibility alias for bookmarks and shared links.
  {
    path: PagePaths.communicationsV2,
    importPath: 'communications-v2',
    permission: Permission.ChatView,
  },
  { path: PagePaths.introducer, importPath: 'referral' },
  { path: PagePaths.introducerHowToEarn, importPath: 'referral/how-to-earn' },
  {
    path: PagePaths.introducerUser,
    importPath: 'referral/admin-referral/_id',
    permission: Permission.ReferralAdminView,
    session: 'admin',
  },
  { path: PagePaths.kycComplete, importPath: 'kyc-complete' },
  {
    path: `${PagePaths.deals}/*`,
    importPath: 'deals/offer-route-layout',
    children: [
      { index: true, element: null },
      {
        path: 'offers/:companyId/:offerId',
        lazy: async () => {
          const context = { source: 'route.lazy', routeImportPath: 'deals/personal/_id' };
          try {
            return { Component: (await import('@/pages/deals/personal/_id')).default };
          } catch (error) {
            if (attemptChunkReloadForError(error, context)) return { Component: () => null };
            reportChunkLoadError(error, context, { action: 'throw' });
            throw error;
          }
        },
      },
    ],
  },
  {
    path: PagePaths.dealsKanban,
    importPath: 'deals-kanban/legacy-redirect',
    session: 'admin',
  },
  // Intentionally ungated by `permission`: this is not the admin board above.
  // Access is self-guarded (GP-3840, variant A) by `useDealBoardViewerPerspective`
  // — an authorised provider or an eligible introducer — never by
  // `Permission.KanbanItemView`, which stays Gamma-only.
  { path: PagePaths.dealsPipeline, importPath: 'deals-pipeline' },

  {
    path: PagePaths.dealsCreateCompany,
    importPath: 'deals/entity-deal/provider-dashboard/modules/create-company',
  },
  {
    path: PagePaths.dealsEditCompany,
    importPath: 'deals/entity-deal/provider-dashboard/modules/edit-company',
    permission: Permission.DealManage,
    session: ['admin', 'entity'],
  },
  {
    path: PagePaths.dealsPostOffer,
    importPath: 'deals/entity-deal/provider-dashboard/modules/post-new-offer',
  },
  { path: PagePaths.dealsCompanyProfile, importPath: 'deals/personal/_id' },

  { path: PagePaths.addressBook, importPath: 'address-book' },
  { path: PagePaths.addressBookEntity, importPath: 'address-book/entity/_id' },
  { path: PagePaths.addressBookCategories, importPath: 'address-book/categories' },
  { path: PagePaths.addressBookCategory, importPath: 'address-book/categories/_id' },
  { path: PagePaths.addressBookImportCallback, importPath: 'address-book/import-callback' },
  { path: PagePaths.contactImportCallback, importPath: 'address-book/import-callback' },

  { path: PagePaths.userProfile, importPath: 'user-profile' },
  { path: PagePaths.addressBookContact, importPath: 'user-profile' },

  // Subscriptions is the reading surface for the profiles you follow. Behind the
  // same flag as the publishing surface itself, so the two release together; with
  // the flag off it falls back to the profile the subscriptions were made from.
  {
    path: PagePaths.subscriptions,
    importPath: 'subscriptions',
    featureFlag: FeatureFlags.USER_PROFILE_V2,
    featureFlagRedirectTo: PagePaths.root,
  },
] as const;

export const withAuthLayoutRoutes: RouteType[] = [
  { path: PagePaths.root, importPath: 'auth/sign-in' },
  // Auth URLs remain stable while their page implementations stay grouped under `pages/auth`.
  { path: PagePaths.signUp, importPath: 'auth/signup' },
  { path: PagePaths.forgotPassword, importPath: 'auth/forgot-password' },
  // GP-3960: a phone camera's own scan of a personal QR link lands here directly, so it must
  // work for both a signed-out and an already-signed-in visitor. `withMainLayoutRoutes` always
  // wraps its routes in `ProtectedRoute`, which would bounce a signed-out scan to root before the
  // page ever mounts and lose the token — so this lives here instead, and the page itself (not a
  // route guard) decides where to send each case. `AuthLayout` is told to leave it alone too; see
  // its `specialPaths`.
  { path: PagePaths.qrConnect, importPath: 'q/_token' },
];

// Reachable signed out and signed in, with no app shell. The page itself decides what each visitor
// sees, and the feature-flag gate lives on the API so a closed flag reads as not-found rather than
// bouncing an anonymous visitor to sign-in.
export const withPublicLayoutRoutes: RouteType[] = [
  { path: PagePaths.publicEvent, importPath: 'e/_slug' },
  { path: PagePaths.getApp, importPath: 'get-app' },
  { path: PagePaths.chatExternalLink, importPath: 'communications-v2/leaving' },
];

export const withFooterLayoutRoutes: RouteType[] = [
  ...(config.IS_DEVELOPMENT ? [{ path: PagePaths.devIcons, importPath: 'dev/icons' }] : []),
];

export const withQuestionnaireLayoutRoutes: RouteType[] = [
  { path: PagePaths.questionnaire, importPath: 'questionnaire' },
] as const;
